import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Sparkles, Upload } from 'lucide-react';
import MakeQuiz from './MakeQuiz';
import AIQuizBuilder from './AIQuizBuilder';
import Navbar from '../Dashboard/NavBar';
import DashboardHeader from '../Dashboard/Header';
import Avatar from '../../Assets/avatar.png';

function QuizCreationChoice() {
  const [searchParams] = useSearchParams();
  const quizId = searchParams.get('quizId');
  const categoryId = searchParams.get('categoryId');

  const [selectedOption, setSelectedOption] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAIBuilder, setShowAIBuilder] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // If quizId is present, automatically open MakeQuiz in edit mode
  useEffect(() => {
    if (quizId) {
      setShowModal(true);
      setSelectedOption('scratch');
    }
  }, [quizId]);

  const handleStartFromScratch = () => {
    setSelectedOption('scratch');
    setShowModal(true);
  };

  const handleBuildWithAI = () => {
    setSelectedOption('ai');
    setShowAIBuilder(true);
  };

  const handleUploadFile = () => {
    setSelectedOption('upload');
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedOption(null);
  };

  const handleGenerateQuiz = () => {
    if (!aiPrompt.trim()) return;
    // TODO: Implement AI generation logic
    console.log('Generating quiz with AI prompt:', aiPrompt);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOption(null);
  };

  const handleCloseAIBuilder = () => {
    setShowAIBuilder(false);
    setSelectedOption(null);
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  return (
    <>
      {/* Page Layout with Navbar and Header */}
      <div className="flex h-screen bg-[#F8FAFC]">
        <Navbar />

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-8 py-4">
            <DashboardHeader
              toggleDropdown={toggleDropdown}
              showDropdown={showDropdown}
              avatar={Avatar}
            />
          </div>

          {/* Choice Canvas */}
          <div
            className="quiz-onboarding-view-wrapper"
            style={{
              backgroundColor: '#F8FAFC',
              width: '100%',
              height: 'calc(100vh - 90px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '32px 24px',
              overflow: 'hidden'
            }}
          >
            {/* Title - Premium Gradient Design */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <Sparkles className="w-6 h-6 text-emerald-500" strokeWidth={2} />
              <h1 className="text-[32px] font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-600 bg-clip-text text-transparent tracking-tight leading-tight">
                Choose how to build your quiz
              </h1>
            </div>

            {/* Cards Container - 3 Column Grid */}
            <div
              className="cards-alignment-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(240px, 320px))',
                gap: '20px',
                width: '100%',
                maxWidth: '1020px',
                marginTop: '0',
                alignItems: 'stretch'
              }}
            >
              {/* Card 1: Start from scratch */}
              <div
                onClick={handleStartFromScratch}
                className="creation-choice-card"
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: '16px',
                  padding: '40px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 4px rgba(15, 23, 42, 0.02)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.borderColor = '#10B981';
                  e.currentTarget.style.boxShadow = '0 16px 32px -4px rgba(15, 23, 42, 0.08), 0 4px 12px -2px rgba(16, 185, 129, 0.04)';
                  const icon = e.currentTarget.querySelector('svg');
                  if (icon) icon.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = '#E2E8F0';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(15, 23, 42, 0.02)';
                  const icon = e.currentTarget.querySelector('svg');
                  if (icon) icon.style.transform = 'scale(1)';
                }}
              >
                <div
                  style={{
                    width: '68px',
                    height: '68px',
                    backgroundColor: 'rgba(16, 185, 129, 0.06)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}
                >
                  <FileText className="w-7 h-7" style={{
                    color: '#10B981',
                    transition: 'transform 0.25s ease'
                  }} />
                </div>
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#0F172A',
                    marginBottom: '10px',
                    textAlign: 'center'
                  }}
                >
                  Start from scratch
                </h3>
                <p
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#64748B',
                    textAlign: 'center',
                    lineHeight: '1.5'
                  }}
                >
                  Build your quiz manually with full control.
                </p>
              </div>

              {/* Card 2: Build with AI */}
              <div
                className="creation-choice-card"
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: '16px',
                  padding: '40px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 4px rgba(15, 23, 42, 0.02)',
                  position: 'relative'
                }}
                onClick={handleBuildWithAI}
                onMouseEnter={(e) => {
                  if (selectedOption !== 'ai') {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.borderColor = '#10B981';
                    e.currentTarget.style.boxShadow = '0 16px 32px -4px rgba(15, 23, 42, 0.08), 0 4px 12px -2px rgba(16, 185, 129, 0.04)';
                    const icon = e.currentTarget.querySelector('svg');
                    if (icon) icon.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedOption !== 'ai') {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = '#E2E8F0';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(15, 23, 42, 0.02)';
                    const icon = e.currentTarget.querySelector('svg');
                    if (icon) icon.style.transform = 'scale(1)';
                  }
                }}
              >
                <div
                  style={{
                    width: '68px',
                    height: '68px',
                    backgroundColor: 'rgba(16, 185, 129, 0.06)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}
                >
                  <Sparkles className="w-7 h-7" style={{
                    color: '#10B981',
                    transition: 'transform 0.25s ease'
                  }} />
                </div>
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#0F172A',
                    marginBottom: '10px',
                    textAlign: 'center'
                  }}
                >
                  Build with AI
                </h3>
                <p
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#64748B',
                    textAlign: 'center',
                    lineHeight: '1.5',
                    marginBottom: selectedOption === 'ai' ? '20px' : '0'
                  }}
                >
                  Let AI generate quiz questions based on a topic.
                </p>

          {/* AI Input Form - Removed, now opens modal instead */}
          {false && selectedOption === 'ai' && (
            <div
              style={{
                width: '100%',
                marginTop: '20px',
                animation: 'fadeIn 0.3s ease-in'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Describe your quiz topic (e.g., 'Create 10 questions about World War II')"
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#1E293B',
                  resize: 'none',
                  marginBottom: '12px',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#6B7A8F';
                  e.target.style.boxShadow = '0 0 0 3px rgba(107, 122, 143, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E2E8F0';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                onClick={handleGenerateQuiz}
                disabled={!aiPrompt.trim()}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor: aiPrompt.trim() ? '#6B7A8F' : '#E2E8F0',
                  color: aiPrompt.trim() ? 'white' : '#94A3B8',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: aiPrompt.trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (aiPrompt.trim()) {
                    e.target.style.backgroundColor = '#5A6A7E';
                  }
                }}
                onMouseLeave={(e) => {
                  if (aiPrompt.trim()) {
                    e.target.style.backgroundColor = '#6B7A8F';
                  }
                }}
              >
                Generate Quiz
              </button>
            </div>
          )}
        </div>

              {/* Card 3: Upload File */}
              <div
                className="creation-choice-card"
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: '16px',
                  padding: '40px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 2px 4px rgba(15, 23, 42, 0.02)',
                  position: 'relative'
                }}
                onClick={handleUploadFile}
                onMouseEnter={(e) => {
                  if (selectedOption !== 'upload') {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.borderColor = '#10B981';
                    e.currentTarget.style.boxShadow = '0 16px 32px -4px rgba(15, 23, 42, 0.08), 0 4px 12px -2px rgba(16, 185, 129, 0.04)';
                    const icon = e.currentTarget.querySelector('svg');
                    if (icon) icon.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedOption !== 'upload') {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = '#E2E8F0';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(15, 23, 42, 0.02)';
                    const icon = e.currentTarget.querySelector('svg');
                    if (icon) icon.style.transform = 'scale(1)';
                  }
                }}
              >
                <div
                  style={{
                    width: '68px',
                    height: '68px',
                    backgroundColor: 'rgba(16, 185, 129, 0.06)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}
                >
                  <Upload className="w-7 h-7" style={{
                    color: '#10B981',
                    transition: 'transform 0.25s ease'
                  }} />
                </div>
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#0F172A',
                    marginBottom: '10px',
                    textAlign: 'center'
                  }}
                >
                  Upload File
                </h3>
                <p
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#64748B',
                    textAlign: 'center',
                    lineHeight: '1.5'
                  }}
                >
                  Upload a document to generate questions instantly.
                </p>
              </div>
      </div>

            <style>
              {`
                @keyframes fadeIn {
                  from {
                    opacity: 0;
                    transform: translateY(-10px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }

                /* Tablet & Mobile Viewport Fallback */
                @media (max-width: 900px) {
                  .quiz-onboarding-view-wrapper {
                    overflow-y: auto !important;
                    height: auto !important;
                  }

                  .cards-alignment-grid {
                    grid-template-columns: 1fr !important;
                    max-width: 360px !important;
                    gap: 16px !important;
                  }
                }
              `}
            </style>
          </div>
        </div>
      </div>

      {/* Manual Creation Modal - Rendered when showModal is true */}
      {showModal && (
        <MakeQuiz
          isOpen={showModal}
          onClose={handleCloseModal}
          initialData={quizId ? { quizId, categoryId } : null}
        />
      )}

      {/* AI Builder Modal - Rendered when showAIBuilder is true */}
      {showAIBuilder && (
        <AIQuizBuilder
          isOpen={showAIBuilder}
          onClose={handleCloseAIBuilder}
        />
      )}

      {/* File Upload Modal */}
      {showUploadModal && (
        <FileUploadModal
          isOpen={showUploadModal}
          onClose={handleCloseUploadModal}
        />
      )}
    </>
  );
}

// File Upload Modal Component
function FileUploadModal({ isOpen, onClose }) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelection = (file) => {
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const validExtensions = ['.pdf', '.docx', '.txt'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      alert('Please upload a PDF, DOCX, or TXT file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleGenerate = () => {
    if (!selectedFile) return;
    console.log('Generating quiz from file:', selectedFile.name);
    // TODO: Implement file upload and generation logic
  };

  const handleCancel = () => {
    setSelectedFile(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleCancel}
    >
      <div
        className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-gray-100">
          <h2 className="text-2xl font-semibold text-slate-900">
            Upload Document
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Upload your document and we'll generate quiz questions automatically
          </p>
        </div>

        {/* Dropzone Area */}
        <div className="px-8 py-10">
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleBrowseClick}
            className={`
              relative flex flex-col items-center justify-center
              w-full min-h-[320px] px-8 py-12
              border-[1.5px] rounded-xl
              transition-all duration-300 ease-out
              cursor-pointer
              ${isDragging
                ? 'border-emerald-500 bg-emerald-50/50 shadow-[0_0_0_4px_rgba(16,185,129,0.08)]'
                : selectedFile
                  ? 'border-emerald-500 bg-emerald-50/30'
                  : 'border-dashed border-emerald-500 bg-slate-50/50 hover:bg-slate-50 hover:shadow-[0_0_0_4px_rgba(16,185,129,0.06)]'
              }
            `}
          >
            {/* Icon */}
            <div className={`
              mb-6 transition-transform duration-300
              ${isDragging ? 'scale-110' : 'scale-100'}
            `}>
              {selectedFile ? (
                <FileText className="w-16 h-16 text-emerald-600" strokeWidth={1.5} />
              ) : (
                <Upload className="w-16 h-16 text-slate-400" strokeWidth={1.5} />
              )}
            </div>

            {/* Text Content */}
            {selectedFile ? (
              <div className="text-center">
                <p className="text-base font-medium text-slate-900 mb-1">
                  {selectedFile.name}
                </p>
                <p className="text-sm text-slate-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                  className="mt-4 text-sm font-medium text-emerald-600 hover:text-emerald-700 underline"
                >
                  Change file
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-base font-medium text-slate-700 mb-2">
                  Drag and drop your file here, or{' '}
                  <span className="text-emerald-600 underline decoration-2 underline-offset-2">
                    browse
                  </span>
                </p>
                <p className="text-sm text-slate-500 mt-3">
                  Supports PDF, DOCX, or TXT (Max 10MB)
                </p>
              </div>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-8 py-6 bg-white rounded-b-2xl">
          <button
            onClick={handleCancel}
            className="px-5 py-2.5 text-base font-medium text-slate-600 bg-white border-2 border-gray-300 hover:border-gray-400 rounded-lg transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={!selectedFile}
            className={`
              px-6 py-2.5 rounded-lg text-sm font-semibold
              transition-all duration-200
              ${selectedFile
                ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-600/25 active:scale-[0.98]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-60'
              }
            `}
          >
            Generate Quiz
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuizCreationChoice;
